<template>
  <div class="orgtn">
    <!-- The node card -->
    <div
      class="orgtn__row"
      :class="{ 'orgtn__row--root': depth === 0 }"
    >
      <!-- expand / collapse toggle (or a spacer when leaf) -->
      <button
        v-if="hasChildren"
        type="button"
        class="orgtn__toggle"
        :aria-label="collapsed ? $t('Expand') : $t('Collapse')"
        @click="collapsed = !collapsed"
      >
        <Icon :type="collapsed ? 'ios-arrow-forward' : 'ios-arrow-down'" />
      </button>
      <span
        v-else
        class="orgtn__toggle orgtn__toggle--spacer"
      />

      <!-- icon by role: root / branch / leaf -->
      <span
        class="orgtn__avatar"
        :class="avatarClass"
      >
        <Icon :type="avatarIcon" />
      </span>

      <!-- name + type badge + members badge -->
      <span class="orgtn__label">
        <span class="orgtn__name">{{ node.name }}</span>
        <span
          v-if="node.entity_type"
          class="orgtn__badge"
        >{{ node.entity_type }}</span>
        <span
          v-if="hasChildren"
          class="orgtn__count"
          :title="$t('Sub-entities')"
        >
          <Icon type="md-git-network" />
          {{ node.children.length }}
        </span>
        <button
          v-if="memberCount > 0"
          type="button"
          class="orgtn__members-badge"
          :title="$t('Show attached users')"
          @click="openMembers"
        >
          <Icon type="md-people" />
          {{ memberCount }}
        </button>
        <button
          v-if="node.client"
          type="button"
          class="orgtn__contracts-link"
          :title="$t('View contracts of this client')"
          @click="goToContracts"
        >
          <Icon type="md-document" />
        </button>
      </span>

      <!-- hover actions -->
      <span
        v-if="canEdit"
        class="orgtn__actions"
      >
        <button
          type="button"
          class="orgtn__action"
          :title="$t('Add child')"
          @click="$emit('add', node)"
        >
          <Icon type="md-add" />
        </button>
        <button
          type="button"
          class="orgtn__action"
          :title="$t('Edit')"
          @click="$emit('edit', node)"
        >
          <Icon type="md-create" />
        </button>
        <button
          type="button"
          class="orgtn__action"
          :title="$t('Move')"
          @click="$emit('move', node)"
        >
          <Icon type="md-swap" />
        </button>
        <button
          type="button"
          class="orgtn__action"
          :title="$t('Attach user')"
          @click="$emit('attach', node)"
        >
          <Icon type="md-person-add" />
        </button>
        <button
          type="button"
          class="orgtn__action orgtn__action--danger"
          :title="$t('Delete')"
          @click="$emit('remove', node)"
        >
          <Icon type="md-trash" />
        </button>
      </span>
    </div>

    <!-- Attached users modal (searchable + paginated — scales to thousands) -->
    <Modal
      v-model="membersModal"
      :title="$t('Users attached to') + ' ' + node.name"
      :width="modalWidth"
      footer-hide
    >
      <div class="orgm">
        <Input
          v-model="search"
          search
          clearable
          :placeholder="$t('Search by name or email')"
          class="orgm__search"
          @on-change="onSearch"
        />
        <Table
          :columns="memberColumns"
          :data="members"
          :loading="membersLoading"
          size="small"
          :no-data-text="$t('No user attached to this entity.')"
          class="orgm__table"
        />
        <div class="orgm__footer">
          <span class="orgm__count">{{ membersTotal }} {{ $t("users") }}</span>
          <Page
            :total="membersTotal"
            :current="membersPage"
            :page-size="pageSize"
            size="small"
            @on-change="onPageChange"
          />
        </div>
      </div>
    </Modal>

    <!-- children -->
    <div
      v-if="hasChildren && !collapsed"
      class="orgtn__children"
    >
      <OrgTreeNode
        v-for="child in node.children"
        :key="child.id"
        :node="child"
        :depth="depth + 1"
        :can-edit="canEdit"
        @add="$emit('add', $event)"
        @edit="$emit('edit', $event)"
        @move="$emit('move', $event)"
        @attach="$emit('attach', $event)"
        @remove="$emit('remove', $event)"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: "OrgTreeNode",
  props: {
    node: { type: Object, required: true },
    depth: { type: Number, default: 0 },
    canEdit: { type: Boolean, default: false },
  },
  data() {
    return {
      collapsed: false,
      membersModal: false,
      members: [],
      membersLoading: false,
      membersTotal: 0,
      membersPage: 1,
      pageSize: 20,
      search: "",
      searchTimer: null,
    };
  },
  computed: {
    hasChildren() {
      return !!(this.node.children && this.node.children.length);
    },
    memberCount() {
      return this.node.member_count || 0;
    },
    modalWidth() {
      // Wide modal, but never wider than ~92% of the viewport.
      if (typeof window !== "undefined") {
        return Math.min(1100, Math.round(window.innerWidth * 0.92));
      }
      return 1100;
    },
    memberColumns() {
      const roleColor = {
        1: "default", // Traveler
        2: "geekblue", // Head of Site
        3: "blue", // Manager
        4: "purple", // Operator
        5: "gold", // Multi-Client Manager
      };
      const cols = [
        { title: this.$t("Name"), key: "full_name", minWidth: 140 },
        { title: this.$t("Email"), key: "email", minWidth: 180, ellipsis: true },
        {
          title: this.$t("Role"),
          width: 130,
          render: (h, { row }) =>
            h(
              "Tag",
              { props: { color: roleColor[row.user_role] || "default" } },
              row.role_label || "-"
            ),
        },
        {
          title: this.$t("Phone"),
          key: "cellphone",
          width: 130,
          render: (h, { row }) => h("span", row.cellphone || "—"),
        },
        {
          title: this.$t("Status"),
          width: 100,
          align: "center",
          render: (h, { row }) =>
            h(
              "Tag",
              { props: { color: row.is_active ? "success" : "default" } },
              row.is_active ? this.$t("Active") : this.$t("Inactive")
            ),
        },
      ];
      if (this.canEdit) {
        cols.push({
          title: this.$t("Actions"),
          width: 80,
          align: "center",
          render: (h, { row }) =>
            h(
              "Button",
              {
                props: { type: "text", size: "small", icon: "md-close" },
                attrs: { title: this.$t("Detach from entity") },
                style: { color: "#ed4014" },
                on: { click: () => this.detach(row) },
              }
            ),
        });
      }
      return cols;
    },
    avatarIcon() {
      if (this.depth === 0) return "md-briefcase";
      return this.hasChildren ? "md-folder" : "md-person";
    },
    avatarClass() {
      if (this.depth === 0) return "orgtn__avatar--root";
      return this.hasChildren ? "orgtn__avatar--branch" : "orgtn__avatar--leaf";
    },
  },
  methods: {
    openMembers() {
      this.membersModal = true;
      this.membersPage = 1;
      this.search = "";
      this.loadMembers();
    },
    async loadMembers() {
      this.membersLoading = true;
      try {
        const { results, count } = await this.organizationStore.fetchEntityMembers(
          this.node.id,
          { page: this.membersPage, page_size: this.pageSize, search: this.search || undefined }
        );
        this.members = results;
        this.membersTotal = count;
      } catch (e) {
        this.$Message.error(this.$t("Could not load users"));
      } finally {
        this.membersLoading = false;
      }
    },
    onSearch() {
      // Debounce: wait for the user to stop typing before querying.
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.membersPage = 1;
        this.loadMembers();
      }, 300);
    },
    onPageChange(page) {
      this.membersPage = page;
      this.loadMembers();
    },
    async detach(member) {
      try {
        await this.organizationStore.detachUser(this.node.id, member.id);
        this.$Message.success(this.$t("User detached"));
        // Reload the current page (counts/pagination stay correct).
        this.loadMembers();
      } catch (e) {
        this.$Message.error(this.$t("Something went wrong"));
      }
    },
    goToContracts() {
      // Shortcut to this entity's client contracts page.
      this.$router.push({ name: "contracts", params: { id: this.node.client } });
    },
  },
};
</script>

<style scoped>
.orgtn {
  position: relative;
}

/* ---- Node row (the card) ---- */
.orgtn__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  margin: 4px 0;
  background: #fff;
  border: 1px solid #eef1f5;
  border-radius: 10px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}
.orgtn__row:hover {
  border-color: #d5e8fb;
  background: #f7fbff;
  box-shadow: 0 2px 8px rgba(45, 140, 240, 0.08);
}
.orgtn__row--root {
  background: #f4f9ff;
  border-color: #dbeafd;
}

/* ---- Toggle ---- */
.orgtn__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  color: #b8bec9;
  cursor: pointer;
  border-radius: 6px;
  flex: 0 0 auto;
  transition: background 0.15s ease, color 0.15s ease;
}
.orgtn__toggle:hover {
  background: #eef2f7;
  color: #2d8cf0;
}
.orgtn__toggle--spacer {
  cursor: default;
}
.orgtn__toggle--spacer:hover {
  background: transparent;
}

/* ---- Avatar (role icon) ---- */
.orgtn__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  font-size: 17px;
  flex: 0 0 auto;
}
.orgtn__avatar--root {
  background: #e8f4ff;
  color: #2d8cf0;
}
.orgtn__avatar--branch {
  background: #fff3e0;
  color: #ff9900;
}
.orgtn__avatar--leaf {
  background: #f0f2f5;
  color: #808695;
}

/* ---- Label ---- */
.orgtn__label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
}
.orgtn__name {
  font-weight: 500;
  color: #1f2d3d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.orgtn__badge {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  background: #f0f2f5;
  color: #808695;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  flex: 0 0 auto;
}
.orgtn__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  background: #eef1f5;
  color: #808695;
  font-size: 11px;
  font-weight: 600;
  flex: 0 0 auto;
}
.orgtn__count .ivu-icon {
  font-size: 12px;
  margin-right: 2px;
}

/* ---- Members badge (clickable) ---- */
.orgtn__members-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 20px;
  padding: 0 8px;
  border: none;
  border-radius: 10px;
  background: #e8f4ff;
  color: #2d8cf0;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  flex: 0 0 auto;
  transition: background 0.15s ease;
}
.orgtn__members-badge:hover,
.orgtn__members-badge--open {
  background: #d5e8fb;
}

/* ---- Contracts shortcut ---- */
.orgtn__contracts-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: #f0f2f5;
  color: #808695;
  font-size: 13px;
  cursor: pointer;
  flex: 0 0 auto;
  transition: background 0.15s ease, color 0.15s ease;
}
.orgtn__contracts-link:hover {
  background: #fff3e0;
  color: #ff9900;
}

/* ---- Members modal ---- */
.orgm__search {
  margin-bottom: 12px;
}
.orgm__table {
  margin-bottom: 12px;
}
.orgm__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.orgm__count {
  color: #808695;
  font-size: 13px;
}

/* ---- Actions (revealed on hover) ---- */
.orgtn__actions {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.15s ease;
  flex: 0 0 auto;
}
.orgtn__row:hover .orgtn__actions {
  opacity: 1;
}
.orgtn__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #9aa2af;
  cursor: pointer;
  border-radius: 6px;
  font-size: 15px;
  transition: background 0.15s ease, color 0.15s ease;
}
.orgtn__action:hover {
  background: #e8f4ff;
  color: #2d8cf0;
}
.orgtn__action--danger:hover {
  background: #ffece8;
  color: #ed4014;
}

/* ---- Children + connector lines ---- */
.orgtn__children {
  position: relative;
  margin-left: 25px;
  padding-left: 20px;
}
/* vertical guide */
.orgtn__children::before {
  content: "";
  position: absolute;
  top: -2px;
  bottom: 20px;
  left: 0;
  width: 2px;
  background: #eaeef3;
  border-radius: 2px;
}
/* horizontal connector to each child */
.orgtn__children > .orgtn > .orgtn__row::before {
  content: "";
  position: absolute;
  left: -20px;
  top: 50%;
  width: 20px;
  height: 2px;
  background: #eaeef3;
}
.orgtn__children > .orgtn > .orgtn__row {
  position: relative;
}
</style>
